import { useEffect, useRef, useState } from "react";
import Dashboard from "./dashboard";
import "./App.css";

const STORAGE_KEY = "smart_planting_session";
const LOGIN_PATH = "/login";
const DASHBOARD_PATH = "/dashboard";
const APP_BASE_URL =
  process.env.REACT_APP_APP_URL ||
  (typeof window !== "undefined"
    ? window.location.origin
    : "https://agrisense.pro");

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agrisense.pro/smart-planting-backend";

/* =========================================================
   SESSION HELPERS
========================================================= */

const normalizeSession = (rawSession) => {
  if (!rawSession || typeof rawSession !== "object") {
    return null;
  }

  const user =
    rawSession.user && typeof rawSession.user === "object"
      ? rawSession.user
      : rawSession;

  const sessionUser = {
    id: user.id ?? user.userId ?? user.user_id ?? rawSession.id ?? null,
    name:
      user.name ??
      user.fullName ??
      user.username ??
      user.email ??
      "User",
    username:
      user.username ??
      user.email ??
      user.name ??
      "user",
    email: user.email ?? "",
    role: user.role ?? rawSession.role ?? "user",
  };

  const expiresAt = Number(
    user.expiresAt ??
      rawSession.expiresAt ??
      rawSession.expires ??
      0
  );

  if (
    sessionUser.id === null &&
    !sessionUser.username &&
    !sessionUser.email &&
    !sessionUser.name
  ) {
    return null;
  }

  const nextSession = {
    ...rawSession,
    ...sessionUser,
    id: sessionUser.id,
    name: sessionUser.name,
    username: sessionUser.username,
    email: sessionUser.email,
    role: sessionUser.role,
    user: { ...sessionUser },
    loggedInAt: rawSession.loggedInAt ?? Date.now(),
    expiresAt:
      expiresAt || Date.now() + 60 * 60 * 1000,
  };

  return nextSession;
};

const getStoredSession = () => {
  try {
    const savedSession = localStorage.getItem(STORAGE_KEY);

    if (!savedSession) {
      return null;
    }

    const parsedSession = JSON.parse(savedSession);
    const session = normalizeSession(parsedSession);

    if (!session) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (
      session.expiresAt &&
      Date.now() > Number(session.expiresAt)
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return session;
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const getCurrentPath = () => {
  if (typeof window === "undefined") {
    return LOGIN_PATH;
  }

  const path = window.location.pathname || "/";

  return path === "/" ? LOGIN_PATH : path;
};

/* =========================================================
   EMAIL MASKING
========================================================= */

const maskEmail = (email) => {
  if (!email || !email.includes("@")) {
    return "your registered email";
  }

  const [username, domain] = email.split("@");

  if (!username) {
    return `•••••@${domain}`;
  }

  if (username.length <= 2) {
    return `${username.charAt(0)}•••••@${domain}`;
  }

  return `${username.charAt(0)}•••••@${domain}`;
};

/* =========================================================
   APP
========================================================= */

function App() {
  /* -------------------------------------------------------
     MAIN APP STATE
  ------------------------------------------------------- */

  const [user, setUser] = useState(() => getStoredSession());

  const [route, setRoute] = useState(getCurrentPath());

  const [form, setForm] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);

  /* -------------------------------------------------------
     EMAIL VERIFICATION STATE
  ------------------------------------------------------- */

  const [verificationRequired, setVerificationRequired] =
    useState(false);

  const [verificationCode, setVerificationCode] =
    useState("");

  const [verificationUser, setVerificationUser] =
    useState(null);

  const [verificationLoading, setVerificationLoading] =
    useState(false);

  const [verificationError, setVerificationError] =
    useState("");

  const verificationInputRefs = useRef([]);

  /* =======================================================
     NAVIGATION
  ======================================================= */

  const navigateTo = (nextPath) => {
    const safePath = nextPath.startsWith("/")
      ? nextPath
      : `/${nextPath}`;

    const routePath = (() => {
      if (typeof window === "undefined") {
        return safePath.split("?")[0];
      }

      try {
        const url = new URL(safePath, window.location.origin);
        return url.pathname || "/";
      } catch (error) {
        return safePath.split("?")[0];
      }
    })();

    if (typeof window !== "undefined") {
      const currentPath = getCurrentPath();

      if (currentPath !== routePath) {
        window.history.pushState({}, "", safePath);
      }
    }

    setRoute(routePath);
  };

  /* =======================================================
     BROWSER BACK/FORWARD
  ======================================================= */

  useEffect(() => {
    const handlePopState = () => {
      setRoute(getCurrentPath());
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener(
        "popstate",
        handlePopState
      );
    };
  }, []);

  /* =======================================================
     SESSION / ROUTING
  ======================================================= */

  useEffect(() => {
    if (!user) {
      localStorage.removeItem(STORAGE_KEY);

      if (
        route !== LOGIN_PATH &&
        route !== "/"
      ) {
        navigateTo(LOGIN_PATH);
      }

      if (route === "/") {
        navigateTo(LOGIN_PATH);
      }

      return;
    }

    if (
      route === "/" ||
      route === LOGIN_PATH
    ) {
      navigateTo(DASHBOARD_PATH);
      return;
    }

    const safeSession = normalizeSession(user);

    if (!safeSession) {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      navigateTo(LOGIN_PATH);
      return;
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(safeSession)
    );
  }, [user, route]);

  /* =======================================================
     LOGIN INPUT
  ======================================================= */

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  /* =======================================================
     LOGIN
  ======================================================= */

  const handleLogin = async (event) => {
    event.preventDefault();

    if (
      !form.username.trim() ||
      !form.password.trim()
    ) {
      setError(
        "Please enter both username and password."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/login.php`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          /*
           * IMPORTANT:
           * Allows PHP session cookies to be stored/sent.
           */
          credentials: "include",

          body: JSON.stringify({
            username: form.username.trim(),
            password: form.password,
          }),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (
        !response.ok ||
        data.success === false
      ) {
        throw new Error(
          data.message ||
            data.error ||
            "Invalid username or password."
        );
      }

      /* ---------------------------------------------------
         EMAIL VERIFICATION REQUIRED
      --------------------------------------------------- */

      if (data.verification_required) {
        const backendUser =
          data.user || {
            id:
              data.id ??
              data.userId ??
              null,

            name:
              data.name ??
              data.fullName ??
              data.username ??
              form.username.trim(),

            username:
              data.username ??
              form.username.trim(),

            email:
              data.email ?? "",

            role:
              data.role ?? "user",
          };

        setVerificationUser(backendUser);

        setVerificationRequired(true);

        setVerificationCode("");

        setVerificationError("");

        /*
         * Do NOT save the user yet.
         *
         * The user is not authenticated until
         * verify-login-code.php confirms the code.
         */

        return;
      }

      /* ---------------------------------------------------
         FALLBACK
         
         This allows the frontend to still work if the
         backend doesn't require verification.
         
         Once your new login.php is installed, the code
         above should normally handle the login.
      --------------------------------------------------- */

      const backendUser =
        data.user || {
          id:
            data.id ??
            data.userId ??
            null,

          name:
            data.name ??
            data.fullName ??
            data.username ??
            form.username.trim(),

          username:
            data.username ??
            form.username.trim(),

          email:
            data.email ?? "",

          role:
            data.role ?? "user",
        };

      const sessionUser = {
        id: backendUser.id ?? null,

        name:
          backendUser.name ??
          backendUser.username ??
          form.username.trim(),

        username:
          backendUser.username ??
          form.username.trim(),

        email:
          backendUser.email ?? "",

        role:
          backendUser.role ?? "user",

        loggedInAt: Date.now(),

        expiresAt:
          Date.now() +
          60 * 60 * 1000,
      };

      const authenticatedSession =
        normalizeSession({
          ...sessionUser,
          user: sessionUser,
        });

      if (!authenticatedSession) {
        throw new Error(
          "Unable to create a valid user session."
        );
      }

      setUser(authenticatedSession);

      setForm({
        username: "",
        password: "",
      });

      navigateTo(DASHBOARD_PATH);
    } catch (loginError) {
      setError(
        loginError.message ||
          "Unable to log in right now."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     VERIFICATION CODE INPUT
  ======================================================= */

  const handleVerificationCodeDigitChange = (
    index,
    event
  ) => {
    const newDigit = event.target.value
      .replace(/\D/g, "")
      .slice(-1);

    const digits = verificationCode.split("");
    digits[index] = newDigit;

    const nextCode = digits
      .join("")
      .slice(0, 6);

    setVerificationCode(nextCode);

    if (verificationError) {
      setVerificationError("");
    }

    if (newDigit && index < 5) {
      verificationInputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerificationCodeKeyDown = (
    index,
    event
  ) => {
    if (
      event.key === "Backspace" &&
      !event.currentTarget.value &&
      index > 0
    ) {
      verificationInputRefs.current[index - 1]?.focus();
    }

    if (
      event.key === "ArrowLeft" &&
      index > 0
    ) {
      event.preventDefault();
      verificationInputRefs.current[index - 1]?.focus();
    }

    if (
      event.key === "ArrowRight" &&
      index < 5
    ) {
      event.preventDefault();
      verificationInputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerificationCodePaste = (event) => {
    event.preventDefault();

    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pastedValue) {
      return;
    }

    const digits = Array.from({ length: 6 }, (_, index) =>
      pastedValue[index] || ""
    );

    const nextCode = digits.join("");
    setVerificationCode(nextCode);

    if (verificationError) {
      setVerificationError("");
    }

    const focusIndex = Math.min(
      pastedValue.length,
      5
    );
    verificationInputRefs.current[focusIndex]?.focus();
  };

  /* =======================================================
     VERIFY LOGIN CODE
  ======================================================= */

  const handleVerifyCode = async (event) => {
    event.preventDefault();

    if (verificationCode.length !== 6) {
      setVerificationError(
        "Please enter the 6-digit verification code."
      );
      return;
    }

    setVerificationLoading(true);
    setVerificationError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/verify-login-code.php`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          /*
           * IMPORTANT:
           * Sends the same PHP session created by login.php.
           */
          credentials: "include",

          body: JSON.stringify({
            code: verificationCode,
          }),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (
        !response.ok ||
        data.success === false
      ) {
        throw new Error(
          data.message ||
            data.error ||
            "Invalid verification code."
        );
      }

      /* ---------------------------------------------------
         VERIFICATION SUCCESSFUL
      --------------------------------------------------- */

      const backendUser =
        data.user ||
        verificationUser || {
          id:
            data.id ??
            data.userId ??
            null,

          name:
            data.name ??
            data.fullName ??
            "User",

          username:
            data.username ?? "",

          email:
            data.email ?? "",

          role:
            data.role ?? "user",

          requires_password_reset:
            data.requires_password_reset ??
            false,
        };

      const requiresPasswordReset = Boolean(
        data.requires_password_reset ??
          backendUser.requires_password_reset ??
          verificationUser?.requires_password_reset ??
          false
      );

      if (requiresPasswordReset) {
        setError(
          "Your password must be updated before continuing. Please contact your administrator."
        );
        setVerificationRequired(false);
        setVerificationCode("");
        setVerificationUser(null);
        setVerificationError("");
        setForm({
          username: "",
          password: "",
        });
        return;
      }

      const sessionUser = {
        id: backendUser.id ?? null,

        name:
          backendUser.name ??
          backendUser.username ??
          "User",

        username:
          backendUser.username ??
          backendUser.email ??
          "user",

        email:
          backendUser.email ?? "",

        role:
          backendUser.role ?? "user",

        loggedInAt: Date.now(),

        expiresAt:
          Date.now() +
          60 * 60 * 1000,
      };

      const authenticatedSession =
        normalizeSession({
          ...sessionUser,
          user: sessionUser,
        });

      if (!authenticatedSession) {
        throw new Error(
          "Unable to create a valid user session."
        );
      }

      setUser(authenticatedSession);

      setVerificationRequired(false);

      setVerificationCode("");

      setVerificationUser(null);

      setVerificationError("");

      setForm({
        username: "",
        password: "",
      });

      navigateTo(DASHBOARD_PATH);
    } catch (verifyError) {
      setVerificationError(
        verifyError.message ||
          "Unable to verify the code."
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  /* =======================================================
     BACK TO LOGIN
  ======================================================= */

  const handleBackToLogin = () => {
    setVerificationRequired(false);

    setVerificationCode("");

    setVerificationUser(null);

    setVerificationError("");

    setError("");

    setForm({
      username: "",
      password: "",
    });

    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/login");
      setRoute(LOGIN_PATH);
    }
  };

  /* =======================================================
     ROUTE WATCHERS
  ======================================================= */

  /* =======================================================
     LOGOUT
  ======================================================= */

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);

    setUser(null);

    setError("");

    setVerificationRequired(false);

    setVerificationCode("");

    setVerificationUser(null);

    setVerificationError("");

    navigateTo(LOGIN_PATH);
  };

  /* =======================================================
     VERIFICATION SCREEN
  ======================================================= */

  if (
    !user &&
    verificationRequired
  ) {
    return (
      <div className="auth-shell">
        <div className="auth-panel">

          <div className="brand-block">
            <div className="brand-icon">
              AS
            </div>

            <div>
              <p className="eyebrow">
                AgriSense
              </p>

              <h1>
                Verify Your Login
              </h1>
            </div>
          </div>

          <p className="subtitle">
            A 6-digit verification code was
            sent to:
          </p>

          <p
            style={{
              textAlign: "center",
              fontWeight: "600",
              marginBottom: "24px",
            }}
          >
            {maskEmail(
              verificationUser?.email
            )}
          </p>

          <form
            className="auth-form"
            onSubmit={handleVerifyCode}
          >

            <div className="field-group">
              <label htmlFor="verification-code-0">
                Verification Code
              </label>

              <div
                className="verification-code-inputs"
                role="group"
                aria-label="Verification code"
              >
                {Array.from({ length: 6 }).map(
                  (_, index) => (
                    <input
                      key={index}
                      id={`verification-code-${index}`}
                      name={`verificationCode${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={
                        verificationCode[index] || ""
                      }
                      onChange={(event) =>
                        handleVerificationCodeDigitChange(
                          index,
                          event
                        )
                      }
                      onKeyDown={(event) =>
                        handleVerificationCodeKeyDown(
                          index,
                          event
                        )
                      }
                      onPaste={handleVerificationCodePaste}
                      ref={(element) => {
                        verificationInputRefs.current[index] =
                          element;
                      }}
                      autoFocus={index === 0}
                      aria-label={`Digit ${index + 1}`}
                      className="verification-digit-input"
                    />
                  )
                )}
              </div>
            </div>

            {verificationError ? (
              <div className="alert-box">
                {verificationError}
              </div>
            ) : null}

            <button
              className="primary-button"
              type="submit"
              disabled={
                verificationLoading ||
                verificationCode.length !== 6
              }
            >
              {verificationLoading
                ? "Verifying..."
                : "Verify"}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              disabled={verificationLoading}
              style={{
                width: "100%",
                marginTop: "10px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "10px",
              }}
            >
              Back to Login
            </button>

          </form>
        </div>
      </div>
    );
  }

  if (!user && route !== DASHBOARD_PATH) {
    return (
      <div className="auth-shell">
        <div className="auth-panel">

          <div className="brand-block">
            <div className="brand-icon">
              AS
            </div>

            <div>
              <p className="eyebrow">
                AgriSense
              </p>

              <h1>
                Welcome back
              </h1>
            </div>
          </div>

          <p className="subtitle">
            Sign in to manage your smart
            irrigation dashboard and planting
            operations.
          </p>

          <form
            className="auth-form"
            onSubmit={handleLogin}
          >

            <div className="field-group">
              <label htmlFor="username">
                Username
              </label>

              <input
                id="username"
                name="username"
                type="text"
                placeholder="Enter your username"
                autoComplete="username"
                value={form.username}
                onChange={handleInputChange}
              />
            </div>

            <div className="field-group">
              <label htmlFor="password">
                Password
              </label>

              <input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                value={form.password}
                onChange={handleInputChange}
              />
            </div>

            {error ? (
              <div className="alert-box">
                {error}
              </div>
            ) : null}

            <button
              className="primary-button"
              type="submit"
              disabled={loading}
            >
              {loading
                ? "Signing in..."
                : "Sign in"}
            </button>

          </form>
        </div>
      </div>
    );
  }

  /* =======================================================
     PREVENT DASHBOARD DISPLAY WITHOUT USER
  ======================================================= */

  if (
    !user &&
    route === DASHBOARD_PATH
  ) {
    return null;
  }

  /* =======================================================
     DASHBOARD
  ======================================================= */

  return (
    <Dashboard
      user={user}
      onLogout={handleLogout}
    />
  );
}

export default App;